import { useState, useEffect } from "react";

const useTimer = (selectedTimer, isRecording, mediaRecorder) => {
    const [elapsedTime, setElapsedTime] = useState(0);
    const [showCompletionPopup, setShowCompletionPopup] = useState(false);
    const [hasCompleted, setHasCompleted] = useState(false);

    // Convert timer string to seconds
    const convertTimerToSeconds = (timerString) => {
        const [minutes, seconds] = timerString.split(':').map(Number);
        return minutes * 60 + seconds;
    };

    // Format seconds back to MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Calculate progress for animation (0 to 1)
    const getProgress = () => {
        if (selectedTimer === "00:00") return 0;
        const totalSeconds = convertTimerToSeconds(selectedTimer);
        return elapsedTime / totalSeconds;
    };

    // Generate conic-gradient style for clockwise fill
    const getProgressStyle = () => {
        const progress = getProgress();
        const degrees = progress * 360;
        return {
            background: `conic-gradient(from 0deg, #BDE6FF ${degrees}deg, transparent ${degrees}deg)`
        };
    };

    // Count up effect
    useEffect(() => {
        let intervalId;
        if (isRecording && !showCompletionPopup && !hasCompleted) {
            intervalId = setInterval(() => {
                setElapsedTime(prev => {
                    const newTime = prev + 1;
                    const totalSeconds = convertTimerToSeconds(selectedTimer);

                    if (newTime >= totalSeconds) {
                        // Recording completed - only show popup once
                        setShowCompletionPopup(true);
                        setHasCompleted(true);

                        // Stop media recorder
                        if (mediaRecorder && mediaRecorder.state === 'recording') {
                            mediaRecorder.stop();
                        }

                        return totalSeconds;
                    }

                    return newTime;
                });
            }, 1000);
        }

        return () => clearInterval(intervalId);
    }, [isRecording, selectedTimer, mediaRecorder, showCompletionPopup, hasCompleted]);

    // Reset hasCompleted when timer changes and there's still time remaining
    // This allows the user to manually start recording again with extended time
    useEffect(() => {
        const totalSeconds = convertTimerToSeconds(selectedTimer);
        if (hasCompleted && elapsedTime < totalSeconds) {
            setHasCompleted(false);
            setShowCompletionPopup(false);
        }
    }, [selectedTimer, elapsedTime, hasCompleted]);

    const resetTimer = () => {
        setElapsedTime(0);
        setShowCompletionPopup(false);
        setHasCompleted(false);
    };

    // Custom function to close completion popup
    const closeCompletionPopup = () => {
        setShowCompletionPopup(false);
    };

    return {
        elapsedTime,
        showCompletionPopup,
        setShowCompletionPopup: closeCompletionPopup,
        formatTime,
        getProgress,
        getProgressStyle,
        resetTimer,
        setElapsedTime
    };
};

export default useTimer;